<template>
  <div>
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span>团购活动</span>
        <div class="header-actions">
          <el-button :loading="loading" @click="loadData">刷新</el-button>
          <el-button
            type="warning"
            plain
            :loading="closingExpired"
            @click="handleCloseExpired"
          >
            处理过期拼团
          </el-button>
        </div>
      </div>

      <el-form :inline="true" :model="query" class="query-form">
        <el-form-item>
          <el-input v-model.trim="query.title" placeholder="活动标题" clearable />
        </el-form-item>
        <el-form-item>
          <el-select v-model="query.status" placeholder="活动状态" clearable>
            <el-option :value="1" label="启用中" />
            <el-option :value="0" label="已停用" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button @click="resetQuery">重置</el-button>
          <el-button type="primary" @click="loadData">查询</el-button>
        </el-form-item>
      </el-form>

      <el-alert
        v-if="loadError"
        class="state-block"
        title="团购活动加载失败"
        type="error"
        :closable="false"
        show-icon
      >
        <template slot>
          <span>可以稍后重试，或先检查后端服务状态。</span>
          <el-button type="text" @click="loadData">重新加载</el-button>
        </template>
      </el-alert>

      <template v-else>
        <el-empty v-if="!loading && !list.length" description="当前没有团购活动" />

        <template v-else>
          <el-table :data="list" border v-loading="loading">
            <el-table-column label="活动商品" min-width="320">
              <template slot-scope="{ row }">
                <div class="product-cell">
                  <el-image class="product-cell__image" :src="row.productImage" fit="cover">
                    <div slot="error" class="product-cell__placeholder">无图</div>
                  </el-image>
                  <div class="product-cell__content">
                    <div class="product-cell__title">{{ row.title }}</div>
                    <div class="product-cell__meta">{{ row.productName || `商品 #${row.productId}` }}</div>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="团购价 / 成团人数" width="180">
              <template slot-scope="{ row }">
                <div>￥{{ formatPrice(row.groupBuyPrice) }}</div>
                <div class="sub-text">{{ row.peopleCount }} 人成团</div>
              </template>
            </el-table-column>
            <el-table-column label="发起方式" width="130">
              <template slot-scope="{ row }">
                <el-tag :type="row.launchMode === 'PLATFORM' ? 'warning' : 'success'">
                  {{ row.launchMode === 'PLATFORM' ? '平台发起' : '用户可发起' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="活动状态" width="120">
              <template slot-scope="{ row }">
                <el-tag :type="row.status === 1 ? 'success' : 'info'">
                  {{ row.status === 1 ? '启用中' : '已停用' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="时间状态" width="120">
              <template slot-scope="{ row }">
                <el-tag :type="timeStatusType(row)">
                  {{ timeStatusText(row) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="拼团进度" width="180">
              <template slot-scope="{ row }">
                <div>拼团中 {{ row.activeRecordCount || 0 }}</div>
                <div class="sub-text">已成团 {{ row.successRecordCount || 0 }}</div>
              </template>
            </el-table-column>
            <el-table-column label="活动时间" min-width="220">
              <template slot-scope="{ row }">
                <div>{{ formatDateTime(row.startTime) }}</div>
                <div class="sub-text">{{ formatDateTime(row.endTime) }}</div>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="420">
              <template slot-scope="{ row }">
                <el-button type="text" @click="goProduct(row.productId)">查看商品</el-button>
                <el-button type="text" @click="goRecords(row.id)">拼团记录</el-button>
                <el-button type="text" @click="goOrders(row.id)">关联订单</el-button>
                <el-button
                  v-if="row.launchMode === 'PLATFORM'"
                  type="text"
                  @click="openPlatformDialog(row)"
                >
                  创建平台团
                </el-button>
                <el-button
                  type="text"
                  :loading="actionId === row.id"
                  @click="toggleStatus(row)"
                >
                  {{ row.status === 1 ? '停用活动' : '启用活动' }}
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <div class="table-footer">
            <div class="table-footer__meta">共 {{ total }} 条</div>
            <el-pagination
              background
              layout="prev, pager, next, sizes"
              :current-page="query.pageNum"
              :page-sizes="[10, 20, 50]"
              :page-size="query.pageSize"
              :total="total"
              @current-change="changePage"
              @size-change="changePageSize"
            />
          </div>
        </template>
      </template>
    </el-card>

    <el-dialog
      title="创建平台拼团"
      :visible.sync="platformDialog.visible"
      width="520px"
      @closed="resetPlatformDialog"
    >
      <el-form label-width="110px">
        <el-form-item label="活动">
          <div>{{ platformDialog.activityTitle || '-' }}</div>
        </el-form-item>
        <el-form-item label="选择规格">
          <el-select
            v-model="platformDialog.skuId"
            placeholder="请选择 SKU"
            filterable
            style="width: 100%"
            :loading="platformDialog.skuLoading"
          >
            <el-option
              v-for="sku in platformDialog.skus"
              :key="sku.id"
              :label="`${sku.skuName} / ￥${formatPrice(sku.price)} / 库存 ${sku.stock}`"
              :value="sku.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="截止时间">
          <el-date-picker
            v-model="platformDialog.expireTime"
            type="datetime"
            value-format="yyyy-MM-ddTHH:mm:ss"
            placeholder="默认使用活动结束时间"
            style="width: 100%"
          />
        </el-form-item>
        <el-alert
          title="平台团创建后，小程序用户只能选择该团参团，不会看到发起新团按钮。"
          type="info"
          :closable="false"
          show-icon
        />
      </el-form>
      <div slot="footer">
        <el-button @click="platformDialog.visible = false">取消</el-button>
        <el-button type="primary" :loading="platformDialog.submitting" @click="createPlatformRecord">创建平台团</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  closeExpiredGroupBuy,
  createPlatformGroupBuyRecord,
  downGroupBuyActivity,
  getGroupBuyActivityPage,
  getProductDetail,
  upGroupBuyActivity
} from '../../api/admin'
import { formatDateTime, formatPrice } from '../../utils/format'

export default {
  data() {
    return {
      query: {
        title: '',
        status: '',
        pageNum: 1,
        pageSize: 10
      },
      list: [],
      total: 0,
      loading: false,
      loadError: false,
      closingExpired: false,
      actionId: null,
      platformDialog: {
        visible: false,
        activityId: null,
        activityTitle: '',
        skuId: '',
        expireTime: '',
        skus: [],
        skuLoading: false,
        submitting: false
      }
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    formatDateTime,
    formatPrice,
    async loadData() {
      this.loading = true
      this.loadError = false
      try {
        const data = await getGroupBuyActivityPage(this.query)
        this.list = data.list || []
        this.total = data.total || 0
      } catch (error) {
        this.loadError = true
      } finally {
        this.loading = false
      }
    },
    resetQuery() {
      this.query = {
        title: '',
        status: '',
        pageNum: 1,
        pageSize: 10
      }
      this.loadData()
    },
    changePage(page) {
      this.query.pageNum = page
      this.loadData()
    },
    changePageSize(size) {
      this.query.pageSize = size
      this.query.pageNum = 1
      this.loadData()
    },
    goProduct(productId) {
      this.$router.push(`/products/${productId}`)
    },
    goOrders(activityId) {
      this.$router.push({
        path: '/orders',
        query: {
          q: JSON.stringify({
            orderNo: '',
            receiverMobile: '',
            orderStatus: '',
            groupBuyActivityId: activityId,
            pageNum: 1,
            pageSize: 20
          })
        }
      })
    },
    goRecords(activityId) {
      this.$router.push({
        path: '/group-buy/records',
        query: {
          q: JSON.stringify({
            activityId,
            status: '',
            pageNum: 1,
            pageSize: 10
          })
        }
      })
    },
    timeStatusText(row) {
      const now = Date.now()
      const start = row.startTime ? new Date(row.startTime).getTime() : null
      const end = row.endTime ? new Date(row.endTime).getTime() : null
      if (start && start > now) return '未开始'
      if (end && end < now) return '已结束'
      return '进行中'
    },
    timeStatusType(row) {
      const text = this.timeStatusText(row)
      if (text === '未开始') return 'info'
      if (text === '已结束') return 'warning'
      return 'success'
    },
    async toggleStatus(row) {
      this.actionId = row.id
      try {
        if (row.status === 1) {
          await downGroupBuyActivity(row.id)
          this.$message.success('团购活动已停用')
        } else {
          await upGroupBuyActivity(row.id)
          this.$message.success('团购活动已启用')
        }
        await this.loadData()
      } catch (error) {
        this.$message.error(error?.message || '团购活动状态更新失败')
      } finally {
        this.actionId = null
      }
    },
    async handleCloseExpired() {
      this.closingExpired = true
      try {
        const summary = await closeExpiredGroupBuy()
        const idleClosedCount = Number(summary?.idleClosedCount || 0)
        const failedRecordCount = Number(summary?.failedRecordCount || 0)
        const refundOrderCount = Number(summary?.refundOrderCount || 0)
        const refundProcessedCount = Number(summary?.refundProcessedCount || 0)
        this.$message.success(
          `已处理过期拼团：空团关闭 ${idleClosedCount} 条，失败关团 ${failedRecordCount} 条，待退款订单 ${refundOrderCount} 笔，已尝试发起退款 ${refundProcessedCount} 笔`
        )
        await this.loadData()
      } catch (error) {
        this.$message.error(error?.message || '过期拼团处理失败')
      } finally {
        this.closingExpired = false
      }
    },
    async openPlatformDialog(row) {
      this.platformDialog.visible = true
      this.platformDialog.activityId = row.id
      this.platformDialog.activityTitle = row.title
      this.platformDialog.expireTime = row.endTime || ''
      this.platformDialog.skuLoading = true
      try {
        const detail = await getProductDetail(row.productId)
        this.platformDialog.skus = (detail.skuList || []).filter(item => Number(item.status) === 1)
        if (this.platformDialog.skus.length === 1) {
          this.platformDialog.skuId = this.platformDialog.skus[0].id
        }
      } catch (error) {
        this.$message.error(error?.message || '活动商品规格加载失败')
        this.platformDialog.visible = false
      } finally {
        this.platformDialog.skuLoading = false
      }
    },
    resetPlatformDialog() {
      this.platformDialog = {
        visible: false,
        activityId: null,
        activityTitle: '',
        skuId: '',
        expireTime: '',
        skus: [],
        skuLoading: false,
        submitting: false
      }
    },
    async createPlatformRecord() {
      if (!this.platformDialog.skuId) {
        this.$message.warning('请选择 SKU')
        return
      }
      this.platformDialog.submitting = true
      try {
        await createPlatformGroupBuyRecord({
          activityId: this.platformDialog.activityId,
          skuId: this.platformDialog.skuId,
          expireTime: this.platformDialog.expireTime || undefined
        })
        this.$message.success('平台拼团已创建')
        this.platformDialog.visible = false
        await this.loadData()
      } catch (error) {
        this.$message.error(error?.message || '平台拼团创建失败')
      } finally {
        this.platformDialog.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.query-form {
  margin-bottom: 12px;
}

.state-block {
  margin-bottom: 16px;
}

.product-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.product-cell__image,
.product-cell__placeholder {
  width: 56px;
  height: 56px;
  border-radius: 10px;
}

.product-cell__placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  color: #9ca3af;
  font-size: 12px;
}

.product-cell__title {
  font-weight: 600;
}

.product-cell__meta,
.sub-text {
  margin-top: 4px;
  color: #6b7280;
  font-size: 13px;
}

.table-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
}

.table-footer__meta {
  color: #6b7280;
  font-size: 13px;
}
</style>
